import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useUiTitle } from '../hooks/useUiTitle';
import { useSystemStatus } from '../hooks/useSystemStatus';
import { useOAuthProviders } from '../hooks/useOAuthProviders';
import { startOAuthLogin } from '../utils/startOAuthLogin';
import GradientBlobBackground from '../components/GradientBlobBackground/GradientBlobBackground';
import RetroGrid from '../components/RetroGrid/RetroGrid';
import KineticText from '../components/KineticText/KineticText';
import logo from '../assets/logo.png';

// This project's own brand purple (tailwind.config.cjs primary-500), the
// same color already used for buttons across the real dashboard.
const ACCENT = '#8b5cf6';

const Login: React.FC = () => {
  const [error, setError] = useState('');
  const [moreOptionsExpanded, setMoreOptionsExpanded] = useState(false);
  const [searchParams] = useSearchParams();
  const uiTitle = useUiTitle();
  const systemStatus = useSystemStatus();
  const { providers: oauthProviders, authServerUrl, janusEnabled, loading } = useOAuthProviders();

  useEffect(() => {
    document.title = uiTitle;
  }, [uiTitle]);

  useEffect(() => {
    // Check for error parameter from URL (e.g., from OAuth callback)
    const urlError = searchParams.get('error');
    if (urlError) {
      setError(decodeURIComponent(urlError));
    }
  }, [searchParams]);

  return (
    <div className="relative z-0 min-h-screen w-full flex flex-col" style={{ background: '#07070b', color: '#f5f5f7' }}>
      <div className="absolute inset-0 -z-10">
        <GradientBlobBackground accent={ACCENT} />
        <RetroGrid angle={65} cellSize={60} opacity={0.22} lineColor="#8b5cf6" />
      </div>

      <header className="relative z-10 flex items-center justify-between flex-wrap gap-4 px-6 py-7">
        <Link to="/welcome" className="flex items-center gap-2.5 transition-colors hover:text-white" style={{ color: '#f5f5f7' }}>
          <img src={logo} alt="" className="h-8 w-8 brightness-0 invert" />
          <KineticText text={uiTitle} as="span" className="text-[15px] tracking-[0.2px]" />
        </Link>
        <div
          className="inline-flex items-center gap-2 text-xs px-3.5 py-[7px] rounded-full"
          style={{ color: '#a8adbb', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <span
            className="w-[7px] h-[7px] rounded-full inline-block"
            style={
              systemStatus === 'operational'
                ? { background: '#34d399', boxShadow: '0 0 8px rgba(52,211,153,0.6)' }
                : systemStatus === 'degraded'
                ? { background: '#f87171', boxShadow: '0 0 8px rgba(248,113,113,0.6)' }
                : { background: '#8a84a0' }
            }
          />
          <span>
            {systemStatus === 'operational' ? 'All systems operational' : systemStatus === 'degraded' ? 'Service disruption' : 'Checking status…'}
          </span>
        </div>
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center p-6">
        <div
          className="w-full max-w-[420px] rounded-[20px] p-9 box-border"
          style={{
            background: 'rgba(18,18,24,0.72)',
            border: '1px solid rgba(255,255,255,0.08)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 24px 60px rgba(0,0,0,0.5)'
          }}
        >
          <div className="text-center mb-8">
            <h1 className="m-0 mb-2.5 text-2xl font-semibold leading-snug">Sign in to {uiTitle}</h1>
            <p className="m-0 text-sm leading-relaxed" style={{ color: '#9ba0ad' }}>
              Access your MCP servers, agents, and skills in one place.
            </p>
          </div>

          {error && (
            <div
              className="p-4 text-sm rounded-lg flex items-start space-x-2 mb-6"
              style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5' }}
            >
              <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Loading state: avoids flashing "no login methods" before the
              fetch resolves */}
          {loading && (
            <div className="flex justify-center py-4">
              <div
                className="animate-spin rounded-full h-6 w-6 border-2 border-t-transparent"
                style={{ borderColor: ACCENT, borderTopColor: 'transparent' }}
              />
            </div>
          )}

          {/* Janus (ADR-0005 sign-in migration, Option B): the registry's own
              resource-identity login against Janus, kept separate from the
              oauthProviders list below since Janus is a different
              authorization server, not an oauth2_providers.yml entry -- see
              /api/auth/auth/janus (registry/auth/routes.py's "/auth/janus"
              route, mounted under the router's "/api/auth" prefix). */}
          {!loading && janusEnabled && (
            <div className="space-y-3">
              <button
                onClick={() => { window.location.href = '/api/auth/auth/janus'; }}
                className="w-full flex items-center justify-center gap-2.5 bg-white text-[#1f1f1f] rounded-[11px] px-4 py-3 text-[14.5px] font-semibold transition-all hover:-translate-y-px hover:shadow-lg"
              >
                <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                </svg>
                <span>Continue with Microsoft</span>
              </button>
            </div>
          )}

          {/* OAuth Providers (e.g. Keycloak). When Janus is also enabled,
              these sit behind a "More sign-in options" disclosure instead of
              a second full-width button, so Microsoft reads as the one
              primary path -- shown directly otherwise. */}
          {!loading && oauthProviders.length > 0 && (
            janusEnabled ? (
              <div className="mt-5 text-center">
                <button
                  type="button"
                  onClick={() => setMoreOptionsExpanded(v => !v)}
                  className="bg-transparent border-0 text-[13px] cursor-pointer p-1 transition-colors"
                  style={{ color: '#9ba0ad' }}
                >
                  {moreOptionsExpanded ? 'Hide other sign-in options' : 'More sign-in options'}
                </button>
                {moreOptionsExpanded && (
                  <div className="mt-3.5 pt-4 space-y-3" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                    {oauthProviders.map(provider => (
                      <button
                        key={provider.name}
                        onClick={() => startOAuthLogin(provider.name, authServerUrl)}
                        className="w-full flex items-center justify-center gap-2.5 bg-white text-[#1f1f1f] rounded-[11px] px-4 py-3 text-[14.5px] font-semibold transition-all hover:-translate-y-px hover:shadow-lg"
                      >
                        <span>Continue with {provider.display_name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {oauthProviders.map(provider => (
                  <button
                    key={provider.name}
                    onClick={() => startOAuthLogin(provider.name, authServerUrl)}
                    className="w-full flex items-center justify-center gap-2.5 bg-white text-[#1f1f1f] rounded-[11px] px-4 py-3 text-[14.5px] font-semibold transition-all hover:-translate-y-px hover:shadow-lg"
                  >
                    <span>Continue with {provider.display_name}</span>
                  </button>
                ))}
              </div>
            )
          )}

          {/* Fallback when no providers are configured */}
          {!loading && !janusEnabled && oauthProviders.length === 0 && (
            <div className="text-center py-4">
              <p className="text-sm" style={{ color: '#9ba0ad' }}>
                No login methods are currently configured.
              </p>
              <p className="text-sm mt-2" style={{ color: '#9ba0ad' }}>
                Please contact your administrator.
              </p>
            </div>
          )}
        </div>
      </main>

      <footer className="relative z-10 text-center py-5 text-xs" style={{ color: '#6b7280' }}>
        <span>{typeof window !== 'undefined' ? window.location.hostname : ''}</span>
      </footer>
    </div>
  );
};

export default Login;
